import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowRight, Check, Shield, TrendingUp, Search, Brain, AlertTriangle, Lock, Users, Target } from 'lucide-react'

export default function LandingPage() {
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.5 }
  }

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  }

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
  }

  return (
    <div className="min-h-screen bg-white text-gray-900 font-sans">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <span className="text-xl font-bold tracking-tight text-gray-900">Closy·刻意</span>
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <a href="#features" className="text-sm font-medium text-gray-600 hover:text-gray-900">产品</a>
              <a href="#how-it-works" className="text-sm font-medium text-gray-600 hover:text-gray-900">解决方案</a>
              <a href="#pricing" className="text-sm font-medium text-gray-600 hover:text-gray-900">价格</a>
              <Link to="/login" className="text-sm font-medium text-gray-900 hover:text-gray-700">登录</Link>
              <Link 
                to="/register" 
                className="bg-primary-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors"
              >
                立即开始使用
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-48 lg:pb-32 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto text-center">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-xl font-semibold text-primary-600 mb-6 tracking-wide uppercase">Closy·刻意</h1>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 tracking-tight mb-8 leading-tight">
            从「靠体感推进」<br className="hidden sm:block" />
            到<span className="text-primary-600">「靠证据赢单」</span>。
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-12 leading-relaxed">
            Closy 是专为 B2B 销售打造的 AI 赢单助手，<br />
            帮你消除主观臆断，用证据驱动每一次推进，让赢单不再是运气。
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link 
              to="/register"
              className="w-full sm:w-auto px-8 py-4 bg-primary-600 text-white rounded-xl font-medium text-lg hover:bg-primary-700 transition-all flex items-center justify-center gap-2 shadow-lg shadow-primary-600/30"
            >
              立即开始使用
              <ArrowRight className="w-5 h-5" />
            </Link>
            <span className="text-sm text-gray-500">（无需对接 CRM，开箱即用）</span>
          </div>
        </motion.div>
      </section>

      {/* Second Hook */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.div {...fadeInUp}>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">
              不是“更多报表”，而是“更少误判”。
            </h3>
            <p className="text-xl text-gray-600">
              Closy 不关注你填写了多少表格，<br />
              只关注你是否真正拿到了<strong className="text-gray-900 font-semibold">决定赢单的证据</strong>。
            </p>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 gap-16 items-center">
          <motion.div 
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={container}
          >
            <h3 className="text-3xl font-bold text-gray-900 mb-8">Closy 如何帮你提升赢单率？</h3>
            <div className="space-y-6">
              {[
                { icon: Search, title: "证据驱动诊断", text: "自动分析纪要/邮件/IM，识别“缺EB”、“停方案层”等致命风险。" },
                { icon: Brain, title: "消除体感幻觉", text: "区分“客户客套”与“真实承诺”，把“感觉不错”还原为客观事实。" },
                { icon: TrendingUp, title: "下一步最佳动作", text: "基于销售五步法，提供“最佳路径”与“止血动作”导航。" },
                { icon: Shield, title: "保护销售", text: "AI 是副驾驶不是裁判，诊断结果仅为你服务，不用于绩效考核。" }
              ].map((feature, idx) => (
                <motion.div key={idx} variants={item} className="flex items-start gap-4">
                  <div className="p-3 bg-primary-50 rounded-lg shrink-0">
                    <feature.icon className="w-6 h-6 text-primary-600" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-1">{feature.title}</h4>
                    <p className="text-gray-600">{feature.text}</p>
                  </div>
                </motion.div>
              ))}
            </div>
            <div className="mt-10 p-6 bg-gray-50 rounded-2xl border border-gray-100">
              <p className="text-gray-600 font-medium">
                无需改变销售习惯，<br />
                只需转发邮件或上传纪要，<br />
                立刻获得赢单洞察。
              </p>
            </div>
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="bg-gray-900 rounded-2xl p-8 text-white shadow-2xl rotate-1 relative overflow-hidden"
          >
            {/* Abstract UI representation of Deal Health Card */}
            <div className="space-y-6 relative z-10">
              <div className="flex items-center justify-between border-b border-gray-700 pb-4">
                <div className="w-32 h-6 bg-gray-700 rounded animate-pulse" />
                <div className="flex gap-2">
                  <div className="w-20 h-6 bg-green-500/20 rounded-full" />
                  <div className="w-8 h-8 bg-gray-700 rounded-full" />
                </div>
              </div>
              
              <div className="space-y-4">
                {/* 致命风险卡片 */}
                <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-5">
                  <div className="flex items-start gap-3 mb-3">
                    <AlertTriangle className="w-5 h-5 text-red-400 shrink-0" />
                    <div>
                      <div className="w-40 h-5 bg-red-400/20 rounded mb-2" />
                      <div className="w-64 h-4 bg-gray-700 rounded opacity-60" />
                    </div>
                  </div>
                  <div className="pl-8 space-y-2">
                    <div className="h-8 bg-red-500/20 rounded-lg w-full" />
                    <div className="h-8 bg-gray-800 rounded-lg w-3/4" />
                  </div>
                </div>

                {/* 证据链卡片 */}
                <div className="bg-gray-800 rounded-xl p-5 border border-gray-700">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-24 h-5 bg-gray-700 rounded" />
                    <div className="w-16 h-4 bg-gray-700 rounded opacity-50" />
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 opacity-70">
                      <div className="w-2 h-2 rounded-full bg-blue-400" />
                      <div className="flex-1 h-4 bg-gray-700 rounded" />
                    </div>
                    <div className="flex items-center gap-3 opacity-70">
                      <div className="w-2 h-2 rounded-full bg-green-400" />
                      <div className="flex-1 h-4 bg-gray-700 rounded" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Background decoration */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-primary-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
          </motion.div>
        </div>
      </section>

      {/* Pain Points */}
      <section className="py-24 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fadeInUp} className="mb-16">
            <h3 className="text-3xl font-bold mb-6">你可能正在经历这些“丢单时刻”：</h3>
          </motion.div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: AlertTriangle, title: "明明聊得很好，最后却输了", text: "没识别出关键人（EB）缺位。" },
              { icon: Search, title: "方案改了十几版，还在比参数", text: "陷入方案层，未触达组织层痛点。" },
              { icon: Target, title: "临门一脚被砍价", text: "丢失了价格解释权，毫无还手之力。" },
              { icon: Users, title: "老板问项目情况", text: "只能回“感觉差不多”，缺乏客观证据支撑。" }
            ].map((pain, idx) => (
              <motion.div 
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-gray-800 p-8 rounded-2xl border border-gray-700"
              >
                <div className="w-12 h-12 bg-gray-700/50 rounded-xl flex items-center justify-center mx-auto mb-6">
                  <pain.icon className="w-6 h-6 text-red-400" />
                </div>
                <h4 className="text-lg font-bold mb-3">{pain.title}</h4>
                <p className="text-gray-400 text-sm leading-relaxed">{pain.text}</p>
              </motion.div>
            ))}
          </div>
          
          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.5 }}
            className="mt-16 text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-primary-200 to-white"
          >
            Closy 为解决这些不确定性而生。
          </motion.div>
        </div>
      </section>

      {/* How it Works */}
      <section id="how-it-works" className="py-24 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div {...fadeInUp}>
          <h3 className="text-3xl font-bold text-gray-900 mb-12">Closy 的工作方式很简单：</h3>
          
          <div className="grid md:grid-cols-3 gap-8 relative">
            <div className="hidden md:block absolute top-8 left-1/6 right-1/6 h-0.5 bg-gradient-to-r from-transparent via-primary-200 to-transparent z-0" />
            
            {[
              { step: "1", title: "导入素材", text: "转发邮件、上传会议纪要或粘贴 IM 记录。" },
              { step: "2", title: "AI 诊断", text: "系统自动提取证据，构建“机会假设树”，识别缺口。" },
              { step: "3", title: "执行处方", text: "获取针对性的行动建议（如：如何约见 EB）。" }
            ].map((item, idx) => (
              <div key={idx} className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm relative z-10">
                <div className="w-16 h-16 bg-primary-50 text-primary-600 rounded-2xl flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                  {item.step}
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h4>
                <p className="text-gray-600 text-sm">{item.text}</p>
              </div>
            ))}
          </div>

          <div className="mt-16 p-8 bg-blue-50 rounded-2xl inline-block text-left border border-blue-100">
            <p className="text-xl text-blue-900 text-center font-medium">
              Closy 不做烦人的 CRM 填表工具，<br />
              它是你随身的<strong className="text-primary-600">赢单军师</strong>。
            </p>
          </div>
        </motion.div>
      </section>

      {/* Target Audience */}
      <section className="py-24 bg-gray-50 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-16 items-center">
             <motion.div 
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-2 md:order-1"
            >
              <h3 className="text-3xl font-bold text-gray-900 mb-8">适合这样的人使用 Closy：</h3>
              <ul className="space-y-4">
                {[
                  "一线大客户销售（KA Sales）",
                  "解决方案专家 / 售前顾问（Presales）",
                  "销售团队管理者",
                  "任何从事复杂 B2B 交易的人"
                ].map((item, idx) => (
                  <li key={idx} className="flex items-center gap-3 text-lg text-gray-700 bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
                    <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center shrink-0">
                      <Check className="w-3.5 h-3.5 text-green-600" />
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
              <p className="mt-8 text-xl text-gray-900 font-medium px-4 border-l-4 border-primary-500">
                如果你受够了“莫名其妙的输单”和“无效的内卷汇报”，<br />
                你会喜欢 Closy。
              </p>
            </motion.div>
            <div className="order-1 md:order-2 relative">
               <div className="absolute inset-0 bg-gradient-to-br from-primary-500/20 to-blue-500/20 rounded-full blur-3xl" />
               <div className="relative bg-white p-8 rounded-3xl shadow-xl border border-gray-100">
                 <div className="flex items-center gap-4 mb-6">
                   <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                     <Users className="w-6 h-6 text-gray-500" />
                   </div>
                   <div>
                     <div className="font-bold text-gray-900">KA 销售团队</div>
                     <div className="text-sm text-gray-500">正在使用 Closy</div>
                   </div>
                 </div>
                 <div className="space-y-3">
                    <div className="h-2 bg-gray-100 rounded w-full" />
                    <div className="h-2 bg-gray-100 rounded w-5/6" />
                    <div className="h-2 bg-gray-100 rounded w-4/6" />
                 </div>
               </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust / MVP */}
      <section className="py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-3xl border border-gray-200 p-8 md:p-12 text-center shadow-lg relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-primary-400 to-blue-500" />
          
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-50 text-blue-700 text-sm font-bold mb-8">
            <Shield className="w-4 h-4" />
            MVP 正式上线 · 信任与安全承诺
          </div>
          
          <h3 className="text-2xl font-bold text-gray-900 mb-8">Closy 严格遵守「销售心理安全机制」</h3>
          
          <div className="grid md:grid-cols-3 gap-8 text-left max-w-4xl mx-auto">
            <div className="p-6 bg-gray-50 rounded-xl">
              <div className="w-10 h-10 bg-white rounded-lg shadow-sm flex items-center justify-center mb-4 text-red-500">
                <Shield className="w-5 h-5" />
              </div>
              <h4 className="font-bold text-gray-900 mb-2">不进入绩效</h4>
              <p className="text-sm text-gray-600">AI 诊断结果不作为个人考核依据，仅供自我提升参考。</p>
            </div>
            
            <div className="p-6 bg-gray-50 rounded-xl">
              <div className="w-10 h-10 bg-white rounded-lg shadow-sm flex items-center justify-center mb-4 text-green-500">
                <Lock className="w-5 h-5" />
              </div>
              <h4 className="font-bold text-gray-900 mb-2">隐私优先</h4>
              <p className="text-sm text-gray-600">数据默认私密，仅本人可见。你的数据只属于你。</p>
            </div>
            
            <div className="p-6 bg-gray-50 rounded-xl">
              <div className="w-10 h-10 bg-white rounded-lg shadow-sm flex items-center justify-center mb-4 text-blue-500">
                <Brain className="w-5 h-5" />
              </div>
              <h4 className="font-bold text-gray-900 mb-2">辅助决策</h4>
              <p className="text-sm text-gray-600">AI 提供风险提示，但最终决策权永远在你手中。</p>
            </div>
          </div>
          
          <p className="mt-10 text-lg text-gray-900 font-medium">
            这是一个站在销售这边的产品。
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-primary-900 text-white text-center relative overflow-hidden">
        <div className="absolute inset-0">
          <div
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage:
                'linear-gradient(to right, rgb(156 163 175 / 0.35) 1px, transparent 1px), linear-gradient(to bottom, rgb(156 163 175 / 0.35) 1px, transparent 1px)',
              backgroundSize: '24px 24px',
            }}
          />
          <div className="absolute top-0 right-0 w-96 h-96 bg-primary-500/30 rounded-full blur-3xl translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-500/30 rounded-full blur-3xl -translate-x-1/2 translate-y-1/2" />
        </div>
        
        <div className="max-w-4xl mx-auto px-4 relative z-10">
          <h2 className="text-4xl font-bold mb-8">开始使用 Closy</h2>
          <p className="text-xl text-primary-100 mb-12">
            拒绝盲目推进，建立赢单自信。
          </p>
          <Link 
            to="/register"
            className="inline-flex items-center gap-2 px-8 py-4 bg-white text-primary-900 rounded-xl font-bold text-lg hover:bg-gray-100 transition-colors shadow-xl"
          >
            立即免费试用
            <ArrowRight className="w-5 h-5" />
          </Link>
          <p className="mt-6 text-sm text-primary-300 opacity-80">
            免费体验核心功能 · 无需信用卡
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white py-12 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="text-center md:text-left">
            <div className="font-bold text-xl mb-2 text-gray-900">Closy·刻意 ©</div>
            <div className="text-gray-500 text-sm">
              证据驱动 · 消除幻觉 · 赢单增长
            </div>
          </div>
          <div className="text-gray-500 text-sm text-center md:text-right">
            隐私优先 · 销售友好 · 长期主义
          </div>
        </div>
      </footer>
    </div>
  )
}
